// Phase 6A — useSuggestedParts hook.
// READ-ONLY suggestion engine. Combines DVI findings + ticket job categories
// into bucketed suggestions. Pensieve source returns [] in 6A.
//
// SAFETY:
//   - No mutations. No writes anywhere.
//   - DVI matching uses ONLY the curated allowlist (no fuzzy inference).
//   - Job categories that don't normalize → no suggestions for that job.
//   - Existing parts on the ticket are filtered out via token-overlap dedup.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { JobOption } from '@/components/tickets/parts/PartsLineFormDialog';
import type { PartsLine } from '@/hooks/parts/usePartsLines';
import type { GroupedSuggestions, PartSuggestion, SuggestionBucket } from '@/lib/parts-suggestions/types';
import {
  FAMILY_COMPANION_PARTS,
  matchDviFamily,
  normalizeJobCategory,
  type CanonicalFamily,
  type CompanionPart,
} from '@/lib/parts-suggestions/familyMaps';

interface Args {
  shopId: string | undefined;
  ticketId: string;
  /** Jobs on this ticket — caller already has them. Each must include category. */
  jobOptions: Array<JobOption & { category?: string | null }>;
  /** Existing parts_lines so we can dedupe what's already there. */
  existingLines: PartsLine[];
  /** Master gate (e.g. ui_partsSuggestions exposure flag). */
  enabled?: boolean;
  /** Phase 6B — Pensieve "Commonly added" gate (ui_pensieveSuggestions). */
  pensieveEnabled?: boolean;
}

interface PensieveRow {
  label: string;
  occurrence_count: number;
  sample_size: number;
  frequency_pct: number; // 0..1
  scope: 'job' | 'family';
}

interface DviFindingRow {
  id: string;
  title: string | null;
  summary: string | null;
  condition: string | null;
}

/** Lowercase + tokenize for dedup comparison. */
function tokenize(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length > 1);
}

/** Token-overlap ratio: |A∩B| / max(|A|, |B|). Returns 0..1. */
function tokenOverlap(a: string, b: string): number {
  const A = new Set(tokenize(a));
  const B = new Set(tokenize(b));
  if (A.size === 0 || B.size === 0) return 0;
  let hit = 0;
  A.forEach((t) => { if (B.has(t)) hit += 1; });
  return hit / Math.max(A.size, B.size);
}

const DEDUP_THRESHOLD = 0.7;

/** Build chip label for a CompanionPart. */
function buildSuggestion(args: {
  part: CompanionPart;
  family: CanonicalFamily;
  bucket: SuggestionBucket;
  source: 'dvi' | 'job';
  reason: string;
  jobId?: string;
  dviFindingId?: string;
}): PartSuggestion {
  const { part, family, bucket, source, reason, jobId, dviFindingId } = args;
  return {
    key: `${source}:${family}:${bucket}:${part.label.toLowerCase()}`,
    label: part.label,
    category: part.category,
    bucket,
    family,
    source,
    reason,
    jobId,
    dviFindingId,
  };
}

function expandFamily(args: {
  family: CanonicalFamily;
  source: 'dvi' | 'job';
  reason: string;
  jobId?: string;
  dviFindingId?: string;
}): PartSuggestion[] {
  const buckets = FAMILY_COMPANION_PARTS[args.family];
  if (!buckets) return [];
  const out: PartSuggestion[] = [];
  (['repair', 'companion', 'supplies'] as SuggestionBucket[]).forEach((bucket) => {
    for (const part of buckets[bucket]) {
      out.push(buildSuggestion({ ...args, part, bucket }));
    }
  });
  return out;
}

export function useSuggestedParts({
  shopId,
  ticketId,
  jobOptions,
  existingLines,
  enabled = true,
  pensieveEnabled = false,
}: Args): GroupedSuggestions {
  // Pull DVI findings for this ticket. RLS already restricts to the shop.
  const { data: dviFindings = [] } = useQuery<DviFindingRow[]>({
    queryKey: ['dvi-findings-for-suggestions', ticketId],
    enabled: !!shopId && !!ticketId && enabled,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('ticket_dvi_findings')
        .select('id, title, summary, condition')
        .eq('ticket_id', ticketId);
      if (error) throw error;
      return (data ?? []) as DviFindingRow[];
    },
  });

  // ----- Phase 6B — Pensieve co-occurrence (per-job RPC fan-out) -----
  // Only fires for jobs whose category normalizes to a canonical family.
  // Cached per (shop, job, family) — meets the perf requirement of "fetch
  // when ticket loads / job changes" without re-running on every render.
  const pensieveTargets = useMemo(() => {
    if (!enabled || !pensieveEnabled || !shopId) return [];
    return jobOptions
      .map((j) => {
        const family = normalizeJobCategory(j.category);
        if (!family) return null;
        return { jobId: j.job_id, jobName: j.name, family };
      })
      .filter((t): t is { jobId: string; jobName: string; family: CanonicalFamily } => !!t);
  }, [enabled, pensieveEnabled, shopId, jobOptions]);

  // Single combined query keyed by all targets — keeps caching predictable
  // and avoids the rules-of-hooks variable-loop-length problem.
  const targetKey = pensieveTargets.map((t) => `${t.jobId}|${t.family}`).join(',');
  const { data: pensieveRows = [] } = useQuery<Array<PensieveRow & { jobId: string; jobName: string }>>({
    queryKey: ['pensieve-cooc', shopId, targetKey],
    enabled: !!shopId && pensieveTargets.length > 0,
    staleTime: 5 * 60 * 1000, // 5 min
    queryFn: async () => {
      const out: Array<PensieveRow & { jobId: string; jobName: string }> = [];
      for (const t of pensieveTargets) {
        const { data, error } = await (supabase as any).rpc('get_job_part_cooccurrence', {
          p_shop_id: shopId,
          p_job_id: t.jobId,
          p_family: t.family,
        });
        if (error) {
           
          console.warn('[pensieve] RPC failed for job', t.jobId, error.message);
          continue;
        }
        for (const row of (data ?? []) as PensieveRow[]) {
          out.push({ ...row, jobId: t.jobId, jobName: t.jobName });
        }
      }
      return out;
    },
  });

  return useMemo<GroupedSuggestions>(() => {
    if (!enabled) return { dvi: [], job: [], pensieve: [] };

    // ----- DVI-driven suggestions (allowlist match only) -----
    const dvi: PartSuggestion[] = [];
    for (const f of dviFindings) {
      const haystack = `${f.title ?? ''} ${f.summary ?? ''} ${f.condition ?? ''}`;
      const family = matchDviFamily(haystack);
      if (!family) continue; // silent miss, by design
      const reasonText = (f.title || f.summary || 'inspection finding').trim();
      const sugs = expandFamily({
        family,
        source: 'dvi',
        reason: `DVI finding: ${reasonText}`,
        dviFindingId: f.id,
      });
      dvi.push(...sugs);
    }

    // ----- Job-driven suggestions (strict normalization only) -----
    const job: PartSuggestion[] = [];
    for (const j of jobOptions) {
      const family = normalizeJobCategory(j.category);
      if (!family) continue; // unknown / ambiguous → no emit
      const sugs = expandFamily({
        family,
        source: 'job',
        reason: `Job: ${j.name}`,
        jobId: j.job_id,
      });
      job.push(...sugs);
    }

    // ----- Pensieve-driven suggestions (Phase 6B) -----
    // All Pensieve items render in the Companion items sub-section only —
    // never repair, never supplies (server already excludes shop_supplies).
    const pensieve: PartSuggestion[] = [];
    if (pensieveEnabled) {
      for (const row of pensieveRows) {
        const pct = Math.round((row.frequency_pct ?? 0) * 100);
        const reason = row.scope === 'job'
          ? `Commonly added on this job (${pct}%, n=${row.sample_size})`
          : `Commonly added in this category (${pct}%, n=${row.sample_size})`;
        // Family is informational only for Pensieve grouping; we use the
        // normalized family of the originating job target.
        const target = pensieveTargets.find((t) => t.jobId === row.jobId);
        const family = target?.family ?? 'engine_perf';
        pensieve.push({
          key: `pensieve:${row.jobId}:${row.scope}:${row.label.toLowerCase()}`,
          label: row.label,
          category: 'parts',
          bucket: 'companion',
          family,
          source: 'pensieve',
          reason,
          jobId: row.jobId,
          pensieveScope: row.scope,
          pensieveFrequencyPct: row.frequency_pct,
          pensieveSampleSize: row.sample_size,
        });
      }
    }

    // ----- Dedupe vs existing lines on ticket -----
    const existingDescs = existingLines.map((l) => l.description ?? '');
    const isAlreadyOnTicket = (label: string): boolean => {
      for (const d of existingDescs) {
        if (!d) continue;
        if (d.toLowerCase().includes(label.toLowerCase())) return true;
        if (tokenOverlap(label, d) >= DEDUP_THRESHOLD) return true;
      }
      return false;
    };

    // ----- Dedupe within group by stable key -----
    const dedupeGroup = (group: PartSuggestion[]): PartSuggestion[] => {
      const seen = new Set<string>();
      const out: PartSuggestion[] = [];
      for (const s of group) {
        if (seen.has(s.key)) continue;
        if (isAlreadyOnTicket(s.label)) continue;
        seen.add(s.key);
        out.push(s);
      }
      return out;
    };

    const dedupedDvi = dedupeGroup(dvi);
    const dedupedJob = dedupeGroup(job);

    // Pensieve must NEVER duplicate labels already shown by DVI or Job groups.
    const upstreamLabels = new Set<string>();
    for (const s of dedupedDvi) upstreamLabels.add(s.label.toLowerCase());
    for (const s of dedupedJob) upstreamLabels.add(s.label.toLowerCase());
    const pensieveFiltered = pensieve.filter((s) => !upstreamLabels.has(s.label.toLowerCase()));
    const dedupedPensieve = dedupeGroup(pensieveFiltered);

    return {
      dvi: dedupedDvi,
      job: dedupedJob,
      pensieve: dedupedPensieve,
    };
  }, [enabled, pensieveEnabled, dviFindings, jobOptions, existingLines, pensieveRows, pensieveTargets]);
}
